import { isNil, get } from 'lodash';

/**
 * Gets the profile id.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The id for the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getId = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, '_id', '');
};

/**
 * Gets profile creation time.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The profile creation time or an empty string if createdAt is undefined.
 * @throws Error if profile object is undefined.
 */
export const getCreation = profileObject => {
  if (isNil(profileObject)) {
    throw new Error('profile not defined');
  }
  return get(profileObject, 'createdAt', '');
};

/**
 * Gets profile last update time.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The profile last update time or an empty string if updatedAt is undefined.
 * @throws Error if profile object is undefined.
 */
export const getLastUpdate = profileObject => {
  if (isNil(profileObject)) {
    throw new Error('profile not defined');
  }
  return get(profileObject, 'updatedAt', '');
};

/**
 * Checks if the profile is deleted.
 *
 * @param {object} profileObject - The profile object.
 * @returns {boolean} True if the profile is in trash, false otherwise.
 * @throws {Error} If profile is null or undefined.
 */
export const isDeleted = profileObject => {
  if (isNil(profileObject)) throw new Error('connection not defined');
  return get(profileObject, 'deleted', '') !== 'no';
};

/**
 * Get profile name.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The name of the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getName = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'name', '');
};

/**
 * Get profile name for the entwine.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The entwine name of the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getEntWineName = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'geoJSON.properties.entWineName', '');
};

/**
 * Get profile path for the track video.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The path of the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getPath = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'geoJSON.properties.path', '');
};

/**
 * Get profile video id asociated.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The id of the video asociated at profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getVideoId = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'geoJSON.properties.videoId', '');
};

/**
 * Get profile buffer.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The buffer of the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getBuffer = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'geoJSON.properties.buffer', '');
};

/**
 * Get profile clasify of the points.
 *
 * @param {object} profileObject - The profile object.
 * @returns {string} The clasify of the profile or empty string if not found.
 * @throws {Error} If profile is null or undefined.
 */
export const getClasify = profileObject => {
  if (isNil(profileObject)) throw new Error('profile not defined');
  return get(profileObject, 'geoJSON.properties.clasify', []);
};
